import React, { FC, memo, useEffect, useState } from "react";
import { Box, Button, IconButton, InputLabel, Typography } from "@mui/material";
import MainRadioGroup from "components/MainRadioGroup";
import DatePicker from "react-datepicker";
import {
  filterFutureTimes,
  isToday,
  locale,
  maxTime,
  minTime,
  timeMenu,
  timeValues,
} from "./constants";
import { ITimeSectionProps } from "./types";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { useAppDispatch } from "store/hooks/useAppDispatch";
import { setArriveTime } from "store/slices/arriveTime";
import { useAppSelector } from "store/hooks/useAppSelector";
import formatDateToISOString from "utils/formatDateToISOString";
import combineDateTime from "utils/combineDateTime";
import "react-datepicker/dist/react-datepicker.css";
import { shallowEqual } from "react-redux";
import styles from "./TimeSection.styled";

const TimeSection: FC<ITimeSectionProps> = ({ onBackClick }) => {
  const timeData = useAppSelector((state) => state.arriveTime);
  const accentColor = useAppSelector(
    (state) => state.settings.config.accentColor,
    shallowEqual,
  );
  const [isPickersVisible, setIsPickersVisible] = useState<boolean>(
    timeData.value === timeValues.preOrder,
  );
  const [isTimePickersVisible, setIsTimePickersVisible] = useState<boolean>(
    timeData.value !== timeValues.now,
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    new Date(timeData.dateTime),
  );
  const [selectedTime, setSelectedTime] = useState<Date | null>(
    new Date(timeData.dateTime),
  );
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (selectedDate && selectedTime && timeData.value !== timeValues.now) {
      const combinedDateTime = new Date(
        combineDateTime(selectedDate, selectedTime),
      );

      if (combinedDateTime < new Date()) {
        setSelectedTime(null);
      }
    }
  }, [selectedDate, selectedTime, timeData.value]);

  const handleTimeWhenChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === timeValues.now) {
      setIsTimePickersVisible(false);
      setIsPickersVisible(false);
      setSelectedDate(new Date());
      setSelectedTime(new Date());
      dispatch(
        setArriveTime({
          value: timeMenu[0].value,
          title: timeMenu[0].title,
          dateTime: formatDateToISOString(new Date()),
          reservation: false,
        }),
      );
    }
    if (event.target.value === timeValues.today) {
      setIsTimePickersVisible(true);
      setIsPickersVisible(false);
      setSelectedDate(new Date());
      setSelectedTime(minTime);
      if (selectedTime) {
        dispatch(
          setArriveTime({
            value: timeMenu[1].value,
            title: timeMenu[1].title,
            dateTime: combineDateTime(new Date(), minTime),
            reservation: true,
          }),
        );
      }
    }
    if (event.target.value === timeValues.preOrder) {
      setIsTimePickersVisible(true);
      setIsPickersVisible(true);
      setSelectedDate(new Date());
      setSelectedTime(minTime);
      if (selectedDate && selectedTime) {
        dispatch(
          setArriveTime({
            value: timeMenu[2].value,
            title: timeMenu[2].title,
            dateTime: combineDateTime(selectedDate, minTime),
            reservation: true,
          }),
        );
      }
    }
  };

  const handleTimeChange = (time: Date | null) => {
    setSelectedTime(time);
    if (time && selectedDate) {
      dispatch(
        setArriveTime({
          value: timeData.value,
          title: timeData.title,
          dateTime: combineDateTime(selectedDate, time),
          reservation: true,
        }),
      );
    }
  };

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    if (date && selectedTime) {
      dispatch(
        setArriveTime({
          value: timeData.value,
          title: timeData.title,
          dateTime: combineDateTime(date, selectedTime),
          reservation: true,
        }),
      );
    }
  };

  return (
    <Box sx={styles.contentWrap}>
      <Box sx={styles.headerWrap}>
        <IconButton onClick={onBackClick}>
          <ArrowBackIcon />
        </IconButton>
        <Typography component="h2" sx={styles.title}>
          Час замовлення
        </Typography>
      </Box>
      <MainRadioGroup
        id="time"
        name="time"
        defaultValue={timeData.value}
        options={timeMenu}
        sx={styles.radioGroup}
        onChange={handleTimeWhenChange}
        columnCount={{
          xs: 12,
          md: 12,
          lg: 12,
        }}
      />
      <Box sx={styles.dateTimeWrap}>
        {isTimePickersVisible && (
          <Box
            sx={{
              ...styles.datePickerMain(accentColor),
              ...styles.datePicker(accentColor),
              ...styles.timePicker,
            }}
          >
            <InputLabel htmlFor="arrivedTime" sx={styles.dateTimeLabel}>
              Час
            </InputLabel>
            <DatePicker
              id="arrivedTime"
              name="arrivedTime"
              selected={selectedTime}
              onChange={handleTimeChange}
              showTimeSelect
              showTimeSelectOnly
              timeIntervals={15}
              dateFormat="HH:mm"
              timeFormat="HH:mm"
              showTimeCaption={false}
              minTime={isToday(selectedDate) ? minTime : undefined}
              maxTime={isToday(selectedDate) ? maxTime : undefined}
              filterTime={
                isToday(selectedDate)
                  ? filterFutureTimes
                  : !selectedDate
                  ? filterFutureTimes
                  : undefined
              }
            />
          </Box>
        )}
        {isPickersVisible && (
          <Box
            sx={{
              ...styles.datePickerMain(accentColor),
              ...styles.datePicker(accentColor),
            }}
          >
            <InputLabel htmlFor="arriveDate" sx={styles.dateTimeLabel}>
              Дата
            </InputLabel>
            <DatePicker
              id="arriveDate"
              name="arriveDate"
              dateFormat="dd/MM/yyyy"
              minDate={new Date()}
              showIcon
              toggleCalendarOnIconClick
              selected={selectedDate}
              onChange={handleDateChange}
              icon={<CalendarTodayIcon />}
              locale={locale}
            />
          </Box>
        )}
      </Box>
      <Button onClick={onBackClick} sx={styles.btn} className="secondary">
        Зберегти
      </Button>
    </Box>
  );
};

export default memo(TimeSection);
